import styled, { keyframes } from "styled-components";
import { useLocation } from "react-router-dom";

const WHATSAPP_NUMBER = "96550604047";
const WHATSAPP_MESSAGE = "Hello! I would like to inquire about your services.";
const EMAIL_ADDRESS = "sales@saharacomputers.com";
const EMAIL_SUBJECT = "Inquiry about your services";
const EMAIL_BODY = "Hello! I would like to inquire about your services.";

const WhatsAppButton = () => {
  const location = useLocation();

  // Hide on crm-auth routes (only show on public pages)
  const isCrmAuthRoute = location.pathname.startsWith("/crm-auth");
  if (isCrmAuthRoute) {
    return null;
  }

  const handleWhatsAppClick = () => {
    const encodedMessage = encodeURIComponent(WHATSAPP_MESSAGE);
    window.location.href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
  };

  const emailHref = `mailto:${EMAIL_ADDRESS}?subject=${encodeURIComponent(EMAIL_SUBJECT)}&body=${encodeURIComponent(EMAIL_BODY)}`;

  return (
    <FloatingButtonsContainer>
      <a href={emailHref} className="floatingBtn emailBtn" aria-label="Email us">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
        <span className="tooltip">Email us</span>
      </a>
      <a href="tel:+96522645937" className="floatingBtn phoneBtn" aria-label="Call us">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
        </svg>
        <span className="tooltip">Call us</span>
      </a>
      <button onClick={handleWhatsAppClick} className="floatingBtn whatsappBtn" aria-label="Chat on WhatsApp">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        <span className="tooltip">Chat with us</span>
      </button>
    </FloatingButtonsContainer>
  );
};

export default WhatsAppButton;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.5);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(37, 211, 102, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0);
  }
`;

const FloatingButtonsContainer = styled.div`
  position: fixed;
  bottom: 30px;
  right: 30px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 1000;

  .floatingBtn {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    transition: all 0.3s ease;
    text-decoration: none;
    position: relative;

    svg {
      width: 26px;
      height: 26px;
    }

    .tooltip {
      position: absolute;
      right: 66px;
      background: var(--card-bg, #fff);
      color: var(--text-primary, #333);
      padding: 8px 16px;
      border-radius: 8px;
      font-family: Poppins, sans-serif;
      font-size: 14px;
      font-weight: 500;
      white-space: nowrap;
      opacity: 0;
      visibility: hidden;
      transform: translateX(10px);
      transition: all 0.3s ease;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

      &::before {
        content: "";
        position: absolute;
        right: -6px;
        top: 50%;
        transform: translateY(-50%);
        border-width: 6px;
        border-style: solid;
        border-color: transparent transparent transparent var(--card-bg, #fff);
      }
    }

    &:hover {
      transform: scale(1.1);

      .tooltip {
        opacity: 1;
        visibility: visible;
        transform: translateX(0);
      }
    }

    &:active {
      transform: scale(1.05);
    }
  }

  .emailBtn {
    background: linear-gradient(135deg, #ea4335 0%, #c5221f 100%);
    box-shadow: 0 4px 20px rgba(234, 67, 53, 0.4);

    &:hover {
      box-shadow: 0 6px 25px rgba(234, 67, 53, 0.5);
    }
  }

  .phoneBtn {
    background: linear-gradient(135deg, #2b3b7c 0%, #1a2550 100%);
    box-shadow: 0 4px 20px rgba(43, 59, 124, 0.4);

    &:hover {
      box-shadow: 0 6px 25px rgba(43, 59, 124, 0.5);
    }
  }

  .whatsappBtn {
    background: #25d366;
    box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4);
    animation: ${pulse} 2s infinite;

    svg {
      width: 30px;
      height: 30px;
    }

    &:hover {
      box-shadow: 0 6px 25px rgba(37, 211, 102, 0.5);
    }
  }

  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    gap: 10px;

    .floatingBtn {
      width: 50px;
      height: 50px;

      svg {
        width: 24px;
        height: 24px;
      }

      .tooltip {
        display: none;
      }
    }

    .whatsappBtn svg {
      width: 26px;
      height: 26px;
    }
  }
`;
